import { useState, useEffect, useRef } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import { useApp } from '../../context/AppContext';
import { getPageCount } from '../../utils/pdfUtils';
import './PDFViewer.css';

// Set up the worker - use the version from node_modules
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString();

const PDFViewer = () => {
  const { currentFile, setCurrentView } = useApp();
  const [currentPage, setCurrentPage] = useState(1);
  const [zoom, setZoom] = useState(1.0);
  const [isLoading, setIsLoading] = useState(true);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pageCount, setPageCount] = useState(0);
  const canvasRef = useRef(null);
  const [showThumbnails, setShowThumbnails] = useState(false);

  useEffect(() => {
    if (!currentFile) return;

    const loadDocument = async () => {
      setIsLoading(true);
      try {
        const arrayBuffer = await currentFile.file.arrayBuffer();
        const loadedPdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        setPdfDoc(loadedPdf);
        setPageCount(loadedPdf.numPages);
        setIsLoading(false);
      } catch (error) {
        console.error('Error loading PDF:', error);
        setIsLoading(false);
      }
    };

    loadDocument();
  }, [currentFile]);

  useEffect(() => {
    if (!pdfDoc || !canvasRef.current) return;

    const renderPage = async () => {
      try {
        const page = await pdfDoc.getPage(currentPage);
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');

        const viewport = page.getViewport({ scale: zoom });
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        const renderContext = {
          canvasContext: context,
          viewport: viewport
        };

        await page.render(renderContext).promise;
      } catch (error) {
        console.error('Error rendering page:', error);
      }
    };

    renderPage();
  }, [pdfDoc, currentPage, zoom]);

  if (!currentFile) {
    return (
      <div className="pdf-viewer-empty">
        <p>No PDF file loaded</p>
      </div>
    );
  }

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.25, 3));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.25, 0.5));
  const handleZoomReset = () => setZoom(1.0);
  const handleFitWidth = () => setZoom(1.0);

  const handlePrevPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));
  const handleNextPage = () => setCurrentPage(prev => Math.min(prev + 1, pageCount));

  return (
    <div className="pdf-viewer-container">
      <div className="pdf-toolbar">
        <div className="toolbar-section">
          <h2 className="file-name">{currentFile.name}</h2>
        </div>

        <div className="toolbar-section toolbar-controls">
          <div className="page-controls">
            <button
              className="btn btn-ghost"
              onClick={handlePrevPage}
              disabled={currentPage === 1}
            >
              ←
            </button>
            <span className="page-indicator">
              {currentPage} / {pageCount}
            </span>
            <button
              className="btn btn-ghost"
              onClick={handleNextPage}
              disabled={currentPage === pageCount}
            >
              →
            </button>
          </div>

          <div className="zoom-controls">
            <button className="btn btn-ghost" onClick={handleZoomOut}>
              −
            </button>
            <span className="zoom-level">{Math.round(zoom * 100)}%</span>
            <button className="btn btn-ghost" onClick={handleZoomIn}>
              +
            </button>
            <button className="btn btn-ghost" onClick={handleZoomReset}>
              Reset
            </button>
          </div>

          <button
            className="btn btn-ghost"
            onClick={() => setShowThumbnails(!showThumbnails)}
          >
            {showThumbnails ? 'Hide' : 'Show'} Thumbnails
          </button>
        </div>

        <div className="toolbar-section toolbar-tools">
          <button
            className="btn btn-primary"
            onClick={() => setCurrentView('sign')}
          >
            Sign
          </button>
          <button
            className="btn btn-primary"
            onClick={() => setCurrentView('form')}
          >
            Add Text
          </button>
          <button
            className="btn btn-primary"
            onClick={() => setCurrentView('redact')}
          >
            Redact
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => setCurrentView('split')}
          >
            Split
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => setCurrentView('merge')}
          >
            Merge
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => setCurrentView('convert')}
          >
            Convert
          </button>
        </div>
      </div>

      <div className="pdf-content">
        {showThumbnails && (
          <div className="thumbnails-sidebar">
            <h3>Pages</h3>
            <div className="thumbnails-list">
              {Array.from({ length: pageCount }, (_, i) => i + 1).map((pageNum) => (
                <div
                  key={pageNum}
                  className={`thumbnail ${pageNum === currentPage ? 'active' : ''}`}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  <div className="thumbnail-number">{pageNum}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="pdf-canvas-container">
          {isLoading ? (
            <div className="loading-state">
              <div className="spinner-lg"></div>
              <p>Loading PDF...</p>
            </div>
          ) : (
            <canvas ref={canvasRef} className="pdf-canvas" />
          )}
        </div>
      </div>
    </div>
  );
};

export default PDFViewer;
